/**
 * User management page component for admins.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { listUsers, updateUser, deleteUser, UserListResponse, UserUpdateRequest } from '../api/users';
import { UserEditModal } from '../components/UserEditModal';
import { EditIcon, TrashIcon } from '../components/Icons';
import { logError } from '../utils/errorLogging';
import { getErrorWithContext } from '../utils/errorMessages';
import './UserManagementPage.css';

export const UserManagementPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<UserListResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState<UserListResponse | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null); // user_id being deleted
  const [sortColumn, setSortColumn] = useState<keyof UserListResponse | ''>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const { isConnected, on, off } = useWebSocket();

  useEffect(() => {
    loadUsers();
  }, []);

  // Register WebSocket event handlers for user updates
  useEffect(() => {
    const handleUserUpdate = async (message: { event: string; data?: any }) => {
      const { event_type, user_id, user_data } = message.data || {};
      
      if (event_type === 'added') {
        // New user was added - add to list if data is provided
        if (user_data) {
          setUsers(prevUsers => {
            // Check if already exists (avoid duplicates)
            const exists = prevUsers.some(u => u.id === user_id);
            if (exists) {
              return prevUsers;
            }
            return [...prevUsers, user_data as UserListResponse];
          });
        } else {
          // Reload if no data provided
          await loadUsers();
        }
      } else if (event_type === 'updated') {
        // User was updated - update in list if data is provided
        if (user_data) {
          setUsers(prevUsers => {
            const index = prevUsers.findIndex(u => u.id === user_id);
            if (index >= 0) {
              const updated = [...prevUsers];
              updated[index] = user_data as UserListResponse;
              return updated;
            } else {
              // Add if doesn't exist yet
              return [...prevUsers, user_data as UserListResponse];
            }
          });
        } else {
          // Reload if no data provided
          await loadUsers();
        }
      } else if (event_type === 'deleted') {
        // User was deleted - remove from list
        setUsers(prevUsers => prevUsers.filter(u => u.id !== user_id));
      }
    };

    const unsubscribe = on('user_update', handleUserUpdate);

    return () => {
      unsubscribe();
    };
  }, [on, off]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const userList = await listUsers();
      setUsers(userList);
    } catch (err) {
      logError('Failed to load users', err);
      setError(getErrorWithContext(err, 'load users'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user: UserListResponse) => {
    setEditingUser(user);
  };

  const handleSave = async (userId: number, updates: UserUpdateRequest) => {
    // This is called after successful update from modal
    // Just refresh the user list and show success
    setError(null);
    setSuccess('User updated successfully');
    await loadUsers();
  };

  const handleDelete = async (userId: number) => {
    setDeleting(userId);
    try {
      setError(null);
      setSuccess(null);
      await deleteUser(userId);
      setSuccess('User deleted successfully');
      setDeleteConfirm(null);
      await loadUsers();
    } catch (err: any) {
      logError('Failed to delete user', err);
      setError(getErrorWithContext(err, 'delete user'));
      setDeleteConfirm(null);
    } finally {
      setDeleting(null);
    }
  };


  const filteredUsers = (users || []).filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.email.toLowerCase().includes(searchLower) ||
      user.name.toLowerCase().includes(searchLower)
    );
  });

  // Sort filtered users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (!sortColumn) return 0;

    let aValue: any = a[sortColumn];
    let bValue: any = b[sortColumn];

    // Handle different data types
    if (sortColumn === 'created_at') {
      aValue = new Date(aValue).getTime();
      bValue = new Date(bValue).getTime();
    } else if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    } else if (typeof aValue === 'boolean') {
      aValue = aValue ? 1 : 0;
      bValue = bValue ? 1 : 0;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (column: keyof UserListResponse) => {
    if (sortColumn === column) {
      // Toggle direction if clicking same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  return (
    <AdminLayout>
      <div className="user-management-page">
        <div className="user-management-container">
          <div className="admin-breadcrumb">
            <Link to="/admin">Admin</Link>
            <span className="admin-breadcrumb-separator">/</span>
            <span className="admin-breadcrumb-current">Users</span>
          </div>
          <h1>User Management</h1>
          <p className="page-description">Manage users, roles, and permissions</p>

          {error && (
            <div className="error-message" role="alert">
              <strong>Error:</strong> {error}
            </div>
          )}

          {success && (
            <div className="success-message" role="alert">
              {success}
            </div>
          )}

          <div className="user-management-controls">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by email or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="user-count">
            Showing {sortedUsers.length} of {users?.length || 0} users
          </div>
        </div>

        {loading ? (
          <div className="loading-message">Loading users...</div>
        ) : (
          <>
            {/* Desktop table view */}
            <div className="user-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th 
                      className="sortable" 
                      onClick={() => handleSort('email')}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSort('email');
                        }
                      }}
                      aria-label="Sort by email"
                    >
                      Email
                      {sortColumn === 'email' && (
                        <span className="sort-indicator">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      className="sortable" 
                      onClick={() => handleSort('name')}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSort('name');
                        }
                      }}
                      aria-label="Sort by name"
                    >
                      Name
                      {sortColumn === 'name' && (
                        <span className="sort-indicator">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      className="sortable" 
                      onClick={() => handleSort('is_active')}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSort('is_active');
                        }
                      }}
                      aria-label="Sort by status"
                    >
                      Status
                      {sortColumn === 'is_active' && (
                        <span className="sort-indicator">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      className="sortable" 
                      onClick={() => handleSort('is_admin')}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSort('is_admin');
                        }
                      }}
                      aria-label="Sort by role"
                    >
                      Role
                      {sortColumn === 'is_admin' && (
                        <span className="sort-indicator">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      className="sortable" 
                      onClick={() => handleSort('created_at')}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSort('created_at');
                        }
                      }}
                      aria-label="Sort by created date"
                    >
                      Created
                      {sortColumn === 'created_at' && (
                        <span className="sort-indicator">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="no-users">
                        {searchTerm ? 'No users found matching your search.' : 'No users found.'}
                      </td>
                    </tr>
                  ) : (
                    sortedUsers.map((user) => (
                      <tr key={user.id}>
                        <td>{user.email}</td>
                        <td>{user.name}</td>
                        <td>
                          <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <span className={`role-badge ${user.is_admin ? 'admin' : 'user'}`}>
                            {user.is_admin ? 'Admin' : 'User'}
                          </span>
                        </td>
                        <td>{formatDate(user.created_at)}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn-edit"
                              onClick={() => handleEdit(user)}
                              title="Edit user details"
                              aria-label="Edit user"
                            >
                              <EditIcon />
                            </button>
                            <button
                              className="btn-delete"
                              onClick={() => setDeleteConfirm(user.id)}
                              disabled={user.id === currentUser?.id}
                              title="Delete user"
                              aria-label="Delete user"
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile card view */}
            <div className="user-cards-container">
              {filteredUsers.length === 0 ? (
                <div className="no-users">
                  {searchTerm ? 'No users found matching your search.' : 'No users found.'}
                </div>
              ) : (
                sortedUsers.map((user) => (
                  <div key={user.id} className="user-card">
                    <div className="user-card-header">
                      <div>
                        <div className="user-card-email">{user.email}</div>
                        <div className="user-card-name">{user.name}</div>
                      </div>
                      <div className="user-card-actions">
                        <button
                          className="btn-edit"
                          onClick={() => handleEdit(user)}
                          title="Edit user details"
                          aria-label="Edit user"
                        >
                          <EditIcon />
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => setDeleteConfirm(user.id)}
                          disabled={user.id === currentUser?.id}
                          title="Delete user"
                          aria-label="Delete user"
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </div>
                    <div className="user-card-badges">
                      <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <span className={`role-badge ${user.is_admin ? 'admin' : 'user'}`}>
                        {user.is_admin ? 'Admin' : 'User'}
                      </span>
                    </div>
                    <div className="user-card-meta">
                      <div className="user-card-date">Created: {formatDate(user.created_at)}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {editingUser && (
          <UserEditModal
            user={editingUser}
            currentUserId={currentUser?.id}
            onClose={() => setEditingUser(null)}
            onSave={handleSave}
          />
        )}

        {deleteConfirm && (() => {
          const userToDelete = users.find(u => u.id === deleteConfirm);
          return userToDelete ? (
            <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h3>Confirm Delete</h3>
                <p>
                  Are you sure you want to delete the user <strong>&quot;{userToDelete.email}&quot;</strong>? This action cannot be undone.
                </p>
                <div className="modal-actions">
                  <button
                    className="btn-cancel"
                    onClick={() => setDeleteConfirm(null)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-confirm-delete"
                    onClick={() => handleDelete(deleteConfirm)}
                    disabled={deleting !== null}
                  >
                    {deleting !== null ? 'Deleting...' : 'Delete User'}
                  </button>
                </div>
              </div>
            </div>
          ) : null;
        })()}
        </div>
      </div>
    </AdminLayout>
  );
};

